import React, { useCallback, useMemo } from 'react';

import { link } from '@aline/routes';
import { TableVirtuoso } from 'react-virtuoso';
import type { TableComponents } from 'react-virtuoso';
import { handleOpenInAlineTab } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { formatAnalysisCellValue } from './analysis-result-rows';
import type { AnalysisColumn, AnalysisRow } from './analysis-result-rows';

/**
 * Cells never wrap, so every row is this tall and the table can size itself to
 * its rows without measuring them.
 */
const ROW_HEIGHT = 28;
const MAX_TABLE_HEIGHT = 240;

const useStyles = makeStyles()(theme => ({
  bodyCell: {
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    height: ROW_HEIGHT,
    maxWidth: 240,
    overflow: 'hidden',
    padding: theme.spacing(0, 1),
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cellLink: {
    '&:hover': { textDecoration: 'underline' },
    color: theme.palette.primary.main,
    textDecoration: 'none',
  },
  empty: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(1.5, 1),
  },
  footer: {
    borderTop: `1px solid ${theme.appColors.rule}`,
    color: theme.palette.text.secondary,
    fontSize: 12,
    padding: theme.spacing(0.5, 1),
  },
  headerCell: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    boxSizing: 'border-box',
    color: theme.palette.text.secondary,
    fontFamily: 'monospace',
    fontSize: 12,
    fontWeight: 400,
    height: ROW_HEIGHT,
    padding: theme.spacing(0, 1),
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  root: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 6,
    minWidth: 0,
    overflow: 'hidden',
  },
  table: {
    /**
     * `collapse` lets the sticky header scroll away from its own border, so the
     * cells keep separate borders with no gap between them.
     */
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: 12,
    width: '100%',
  },
}));

interface AnalysisResultTableProps {
  columns: AnalysisColumn[];
  /** The line under the table: how many rows there are, or how many are loaded. */
  footer: string;
  rows: AnalysisRow[];
  /** Called when the reader scrolls to the last loaded row. */
  onEndReached?: () => void;
}

/**
 * The rows an analysis query returned, as a table that scrolls inside a fixed
 * height so a long result does not push the rest of the chat away. Only the
 * rows in view are in the page, so a result of thousands of rows stays cheap.
 */
export const AnalysisResultTable = React.memo(function AnalysisResultTable({
  columns,
  footer,
  onEndReached,
  rows,
}: AnalysisResultTableProps): JSX.Element {
  const { classes } = useStyles();
  const components = useMemo(() => getTableComponents(classes.table), [classes.table]);
  const renderHeader = useCallback(
    () => (
      <tr>
        {columns.map(column => (
          <th key={column.key} className={classes.headerCell} scope="col">
            {column.label}
          </th>
        ))}
      </tr>
    ),
    [classes.headerCell, columns]
  );
  const renderRow = useCallback(
    (_index: number, row: AnalysisRow) =>
      columns.map(column => <AnalysisResultCell key={column.key} column={column.key} row={row} />),
    [columns]
  );

  if (rows.length === 0) return <div className={classes.empty}>The query returned no rows.</div>;

  return (
    <div className={classes.root}>
      <TableVirtuoso
        components={components}
        data={rows}
        endReached={onEndReached}
        fixedHeaderContent={renderHeader}
        fixedItemHeight={ROW_HEIGHT}
        itemContent={renderRow}
        style={{ height: Math.min(MAX_TABLE_HEIGHT, ROW_HEIGHT * (rows.length + 1)) }}
      />
      <div className={classes.footer}>{footer}</div>
    </div>
  );
});

function getTableComponents(tableClassName: string): TableComponents<AnalysisRow> {
  return {
    Table: props => <table {...props} className={tableClassName} />,
  };
}

/**
 * One cell. Only a list row describes a document, so only a `name` cell on a
 * row that has a `documentId` becomes a link.
 */
function AnalysisResultCell({ column, row }: { column: string; row: AnalysisRow }): JSX.Element {
  const { classes } = useStyles();
  const display = formatAnalysisCellValue(row[column]);
  const { documentId } = row;

  if (column !== 'name' || !documentId)
    return (
      <td className={classes.bodyCell} title={display}>
        {display}
      </td>
    );

  const documentRoute = link.DocumentDetail({ documentId });
  return (
    <td className={classes.bodyCell} title={display}>
      <a
        className={classes.cellLink}
        href={documentRoute}
        rel="noreferrer"
        target="_blank"
        onClick={event => handleOpenInAlineTab(event, documentRoute, { title: display })}
      >
        {display}
      </a>
    </td>
  );
}
